import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Brain, Sparkles, Terminal, Zap, Cpu } from "lucide-react";
import { useNavigate } from "react-router-dom";

const AIInstantSolutionSection = () => {
  const navigate = useNavigate();
  return (
    <SectionWrapper id="ai-solution">
      <ContentWrapper>
        <LeftContent
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>
            AI Instant Code Issue <Highlight>Solution</Highlight>
          </Title>
          <Subtitle>
            Paste your code, describe your problem, and get AI-powered instant
            debugging suggestions. Save time, learn from explanations, and fix
            your code faster than ever — powered by{" "}
            <strong>SolveMyCodeIssue AI</strong>.
          </Subtitle>

          <FeatureList>
            <li>
              <Brain size={18} /> Get AI-generated instant fixes for your code
            </li>
            <li>
              <Sparkles size={18} /> Understand errors with step-by-step
              explanations
            </li>
            <li>
              <Terminal size={18} /> Supports JavaScript, Python, React, and
              more
            </li>
            <li>
              <Zap size={18} /> Debug smarter, not harder — save hours of effort
            </li>
          </FeatureList>

          <CTAButton onClick={() => navigate("/post-code-issue")}>
            Try AI Fix Now ⚡
          </CTAButton>
        </LeftContent>

        <RightVisual
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <VisualCard
            whileHover={{ rotate: 2, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            style={{ top: "60px", left: "50px" }}
          >
            <Cpu size={50} />
            <CardTitle>AI Detected Error</CardTitle>
            <CardText>“Unhandled promise rejection in async function”</CardText>
          </VisualCard>

          <VisualCard
            whileHover={{ rotate: -3, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            style={{ top: "250px", left: "150px" }}
          >
            <Brain size={50} />
            <CardTitle>AI Suggested Fix</CardTitle>
            <CardText>
              Add <code>try...catch</code> block or use <code>.catch()</code> to
              handle rejections.
            </CardText>
          </VisualCard>

          <VisualCard
            whileHover={{ rotate: 3, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            style={{ top: "400px", left: "20px" }}
          >
            <Sparkles size={50} />
            <CardTitle>Code Fixed ✅</CardTitle>
            <CardText>Resolved and ready to run successfully!</CardText>
          </VisualCard>
        </RightVisual>
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default AIInstantSolutionSection;

// ---------------- Styled Components ----------------

const SectionWrapper = styled.section`
  width: 100%;
  background: radial-gradient(circle at bottom right, #0b0617, #000);
  color: #fff;
  padding: 120px 40px;
  display: flex;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  gap: 40px;
`;

const LeftContent = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  max-width: 550px;
`;

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 20px;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #7b61ff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #d2cde9;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1rem;
    color: #e0e0e0;
    svg {
      color: #c59fff;
    }
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(90deg, #7b61ff, #c59fff);
  color: white;
  font-weight: 600;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;
  box-shadow: 0 0 15px rgba(123, 97, 255, 0.3);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 25px rgba(123, 97, 255, 0.6);
  }
`;

const RightVisual = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  max-width: 500px;
  position: relative;
  height: 520px;
`;

const VisualCard = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(123, 97, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 260px;
  backdrop-filter: blur(6px);
  text-align: center;
  box-shadow: 0 0 15px rgba(123, 97, 255, 0.2);
  color: #fff;
`;

const CardTitle = styled.h3`
  color: #c59fff;
  margin-top: 12px;
  font-size: 1.1rem;
`;

const CardText = styled.p`
  font-size: 0.9rem;
  color: #ddd;
  margin-top: 6px;
  code {
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 0.85rem;
  }
`;
